import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Flame, Zap, MapPin, BarChart3, TrendingUp, Snowflake,
  Loader2, Trophy,
} from 'lucide-react';
import {
  fetchStreaks, enrollStreak, unenrollStreak, freezeStreak,
  type StreakType,
} from '@/lib/api';
import NamedIcon from '@/components/ui/NamedIcon';
import styles from './StreaksPage.module.css';

interface Props { onNavigate: (path: string) => void }

/**
 * Cadence streaks hub — driver picks from the 5-type menu. Enrolled
 * streaks display progress + current reward; broken ones that have
 * freeze offered get the Freeze CTA.
 *
 * Evaluation happens server-side on the hourly cron; this page is a
 * read-after-refresh affair. We invalidate the query on every mutation
 * so the UI reflects the server's view.
 */

const METRIC_ICON = {
  sessions: Zap,
  kwh: BarChart3,
  unique_stations: MapPin,
  avg_power_kw: TrendingUp,
} as const;

function metricLabel(
  t: (key: string, options?: Record<string, unknown>) => string,
  metric: StreakType['metric'],
  threshold: number,
): string {
  switch (metric) {
    case 'sessions': return t('metric.sessions', { count: threshold });
    case 'kwh': return t('metric.kwh', { count: threshold });
    case 'unique_stations': return t('metric.stations', { count: threshold });
    case 'avg_power_kw': return t('metric.avgPower', { count: threshold });
  }
}

export function StreaksPage({ onNavigate }: Props) {
  const { t } = useTranslation('streaks');
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['streaks', 'list'],
    queryFn: fetchStreaks,
    staleTime: 30_000,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['streaks'] });

  if (q.isLoading && !q.data) {
    return <div className={styles.page}><p className={styles.loading}>{t('loading')}</p></div>;
  }
  if (!q.data) return null;

  const { types, freezeCostPoints, weeklyReward, fourWeekBonus } = q.data;
  const active = types.filter((t) => t.enrolled);
  const available = types.filter((t) => !t.enrolled);

  return (
    <div className={styles.page}>
      <button className={styles.backFab} onClick={() => onNavigate('/profile')} aria-label={t('back')}>
        <ArrowLeft size={18} />
      </button>

      <header className={styles.header}>
        <div className={styles.headerIcon}>
          <NamedIcon name="flame" fallback={<Flame size={20} />} size={20} />
        </div>
        <div>
          <h1 className={styles.title}>{t('title')}</h1>
          <p className={styles.subtitle}>
            {t('subtitle', { weeklyReward, fourWeekBonus })}
          </p>
        </div>
      </header>

      {active.length > 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionLabel}>{t('section.active')}</h2>
          {active.map((t) => (
            <ActiveStreakCard
              key={t.code}
              streak={t}
              freezeCost={freezeCostPoints}
              onAction={refresh}
            />
          ))}
        </section>
      )}

      {available.length > 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionLabel}>{t('section.available')}</h2>
          {available.map((t) => (
            <AvailableStreakCard key={t.code} streak={t} onEnroll={refresh} />
          ))}
        </section>
      )}
    </div>
  );
}

function ActiveStreakCard({
  streak, freezeCost, onAction,
}: {
  streak: StreakType; freezeCost: number; onAction: () => void;
}) {
  const { t } = useTranslation('streaks');
  const [busy, setBusy] = useState<'freeze' | 'unenroll' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const Icon = METRIC_ICON[streak.metric] ?? Zap;
  const offeringFreeze = Boolean(streak.freezeOfferedAt);

  const handleFreeze = async () => {
    setBusy('freeze'); setError(null);
    try { await freezeStreak(streak.code); onAction(); }
    catch (err: unknown) {
      setError((err as { message?: string }).message ?? t('active.freezeError'));
    } finally { setBusy(null); }
  };
  const handleUnenroll = async () => {
    if (!window.confirm(t('active.confirmStop', { name: streak.name }))) return;
    setBusy('unenroll'); setError(null);
    try { await unenrollStreak(streak.code); onAction(); }
    catch (err: unknown) {
      setError((err as { message?: string }).message ?? t('active.unenrollError'));
    } finally { setBusy(null); }
  };

  return (
    <div className={`${styles.card} ${offeringFreeze ? styles.cardAtRisk : ''}`}>
      <div className={styles.cardHead}>
        <span className={styles.cardIcon}><Icon size={18} /></span>
        <div className={styles.cardHeadText}>
          <p className={styles.cardName}>{streak.name}</p>
          <p className={styles.cardSub}>{streak.description}</p>
        </div>
        <div className={styles.cardCount}>
          <span className={styles.cardCountVal}>{streak.currentWeekCount}</span>
          <span className={styles.cardCountLabel}>{t('active.weeksUnit')}</span>
        </div>
      </div>

      {streak.longestWeekCount > 0 && (
        <p className={styles.cardMeta}>
          <Trophy size={11} /> {t('active.longest', { count: streak.longestWeekCount })}
        </p>
      )}

      {offeringFreeze && (
        <div className={styles.atRiskBanner}>
          <Snowflake size={14} />
          <span>
            {t('active.atRisk.lead')} <strong>{t('active.atRisk.cost', { count: freezeCost })}</strong> {t('active.atRisk.trail')}
          </span>
        </div>
      )}

      {error && <p className={styles.error}>{error}</p>}

      <div className={styles.cardActions}>
        {offeringFreeze && (
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={handleFreeze}
            disabled={busy !== null}
          >
            {busy === 'freeze'
              ? <><Loader2 size={13} className={styles.spinner} /> {t('active.freezing')}</>
              : <><Snowflake size={13} /> {t('active.freeze', { count: freezeCost })}</>}
          </button>
        )}
        <button
          type="button"
          className={styles.ghostBtn}
          onClick={handleUnenroll}
          disabled={busy !== null}
        >
          {busy === 'unenroll'
            ? <><Loader2 size={13} className={styles.spinner} /> …</>
            : t('active.unenroll')}
        </button>
      </div>
    </div>
  );
}

function AvailableStreakCard({
  streak, onEnroll,
}: { streak: StreakType; onEnroll: () => void }) {
  const { t } = useTranslation('streaks');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const Icon = METRIC_ICON[streak.metric] ?? Zap;

  const handleEnroll = async () => {
    setBusy(true); setError(null);
    try { await enrollStreak(streak.code); onEnroll(); }
    catch (err: unknown) {
      setError((err as { message?: string }).message ?? t('available.enrollError'));
    } finally { setBusy(false); }
  };

  return (
    <div className={styles.card}>
      <div className={styles.cardHead}>
        <span className={styles.cardIcon}><Icon size={18} /></span>
        <div className={styles.cardHeadText}>
          <p className={styles.cardName}>{streak.name}</p>
          <p className={styles.cardSub}>{streak.description}</p>
        </div>
      </div>
      <p className={styles.cardMeta}>
        {t('available.target')} {metricLabel(t, streak.metric, streak.threshold)}
        <span className={styles.dot}>·</span>
        {t('available.window', { count: streak.windowDays })}
        {streak.longestWeekCount > 0 && (
          <>
            <span className={styles.dot}>·</span>
            {t('available.best', { count: streak.longestWeekCount })}
          </>
        )}
      </p>
      {error && <p className={styles.error}>{error}</p>}
      <div className={styles.cardActions}>
        <button
          type="button"
          className={styles.primaryBtn}
          onClick={handleEnroll}
          disabled={busy}
        >
          {busy ? <><Loader2 size={13} className={styles.spinner} /> {t('available.starting')}</> : t('available.start')}
        </button>
      </div>
    </div>
  );
}
